<script setup>
/**
 * "This week" (spec §5 P-5.4): what the assistant did in the last 7 whole days, counted in the
 * account's own timezone, with the dates shown, and her own "Email me this every week".
 *
 * NUMBERS ARE THE SERVER'S. `week` is `wasloApi.getWeek`'s answer; nothing here adds, rounds or
 * guesses. A week that could not be read says so; it never shows zeros.
 *
 * THE SWITCH SAVES THE MOMENT IT IS FLIPPED (the one switch model, P-5.1), and so does the day.
 * The parent renders the saved answer (`emailSaved`); a failed write remounts both controls, so
 * they cannot sit in a position the server is not in. The setting is hers alone: the BFF keys it
 * by the address her session verified, and this shows that address so she knows where it goes.
 */
import { computed, ref } from 'vue';

import CardLayout from 'dashboard/components-next/CardLayout.vue';
import Banner from 'dashboard/components-next/banner/Banner.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';

import StatTile from './StatTile.vue';
import WasloSelect from './WasloSelect.vue';
import { useWasloI18n } from '../i18n.js';
import { toIntlLocale } from '../helpers/intl.js';
import { wasloApi } from '../api/client.js';

const props = defineProps({
  /** `{ from, to, conversations, handovers, handledWithoutTeamPct, tasksFinished, creditsUsed }`, or null. */
  week: { type: Object, default: null },
  /** `{ enabled, weekday, address }`, or null: the email row is then not drawn. */
  email: { type: Object, default: null },
  accountId: { type: Number, default: null },
});

const emit = defineEmits(['emailSaved']);

const { t, locale } = useWasloI18n();

const intlLocale = computed(() => toIntlLocale(locale.value));
const count = value => new Intl.NumberFormat(intlLocale.value).format(value);

/** A calendar date the server already named in her zone, so it is never shifted again here. */
const dayOf = day =>
  new Intl.DateTimeFormat(intlLocale.value, {
    day: 'numeric',
    month: 'long',
    timeZone: 'UTC',
  }).format(new Date(`${day}T00:00:00.000Z`));

const dates = computed(() =>
  props.week
    ? t('WASLO.OVERVIEW.WEEK.DATES', {
        from: dayOf(props.week.from),
        to: dayOf(props.week.to),
      })
    : ''
);

const handled = computed(() => {
  const pct = props.week?.handledWithoutTeamPct;
  if (typeof pct !== 'number') return t('WASLO.OVERVIEW.WEEK.HANDLED_NONE');
  return new Intl.NumberFormat(intlLocale.value, {
    style: 'percent',
    maximumFractionDigits: 0,
  }).format(pct / 100);
});

const tiles = computed(() => {
  const week = props.week;
  if (!week) return [];
  return [
    {
      key: 'conversations',
      label: t('WASLO.OVERVIEW.WEEK.CONVERSATIONS'),
      value: count(week.conversations),
    },
    {
      key: 'handled',
      label: t('WASLO.OVERVIEW.WEEK.HANDLED'),
      value: handled.value,
    },
    {
      key: 'handovers',
      label: t('WASLO.OVERVIEW.WEEK.HANDOVERS'),
      value: count(week.handovers),
    },
    {
      key: 'tasks',
      label: t('WASLO.OVERVIEW.WEEK.TASKS'),
      value: count(week.tasksFinished),
    },
    {
      key: 'credits',
      label: t('WASLO.OVERVIEW.WEEK.CREDITS'),
      value: count(week.creditsUsed),
    },
  ];
});

/** 4 January 2026 is a Sunday, and the server counts 0 = Sunday. */
const SUNDAY = Date.parse('2026-01-04T00:00:00.000Z');
const weekdayOptions = computed(() =>
  Array.from({ length: 7 }, (_, index) => ({
    value: String(index),
    label: new Intl.DateTimeFormat(intlLocale.value, {
      weekday: 'long',
      timeZone: 'UTC',
    }).format(new Date(SUNDAY + index * 86_400_000)),
  }))
);

const saving = ref(false);
const failed = ref(false);
const epoch = ref(0);
/** The email is written in the language she is reading now. */
const screenLocale = computed(() =>
  String(locale.value).startsWith('ar') ? 'ar' : 'en'
);

const save = async next => {
  if (saving.value || props.accountId === null || !props.email) {
    epoch.value += 1;
    return;
  }
  saving.value = true;
  failed.value = false;
  try {
    const saved = await wasloApi.updateWeekEmail(props.accountId, {
      enabled: next.enabled,
      weekday: next.weekday,
      locale: screenLocale.value,
    });
    emit('emailSaved', saved);
  } catch {
    failed.value = true;
    epoch.value += 1;
  } finally {
    saving.value = false;
  }
};

const onToggle = enabled => save({ enabled, weekday: props.email.weekday });
const onDay = value =>
  save({ enabled: props.email.enabled, weekday: Number(value) });
</script>

<template>
  <div class="flex flex-col gap-4" data-waslo-week>
    <div class="flex flex-col gap-1">
      <h2 class="mb-0 text-base font-medium text-n-slate-12">
        {{ t('WASLO.OVERVIEW.WEEK.HEADING') }}
      </h2>
      <p
        v-if="dates"
        class="mb-0 text-sm text-n-slate-11"
        data-waslo-week-dates
      >
        {{ dates }}
      </p>
    </div>

    <Banner v-if="!week" color="slate" data-waslo-week-unavailable>
      {{ t('WASLO.OVERVIEW.WEEK.UNAVAILABLE') }}
    </Banner>
    <div v-else class="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
      <StatTile
        v-for="tile in tiles"
        :key="tile.key"
        :label="tile.label"
        :value="tile.value"
        :data-waslo-week-tile="tile.key"
      />
    </div>

    <CardLayout v-if="email" data-waslo-week-email>
      <div class="flex flex-col gap-3">
        <div class="flex items-center gap-2">
          <Switch
            :key="`switch-${epoch}`"
            :model-value="email.enabled"
            :aria-label="t('WASLO.OVERVIEW.WEEK.EMAIL_LABEL')"
            data-waslo-week-email-switch
            @update:model-value="onToggle"
          />
          <span class="text-sm text-n-slate-12">
            {{ t('WASLO.OVERVIEW.WEEK.EMAIL_LABEL') }}
          </span>
        </div>
        <template v-if="email.enabled">
          <WasloSelect
            :key="`day-${epoch}`"
            class="max-w-xs"
            :model-value="String(email.weekday)"
            :label="t('WASLO.OVERVIEW.WEEK.EMAIL_DAY')"
            :options="weekdayOptions"
            data-waslo-week-email-day
            @update:model-value="onDay"
          />
          <p class="mb-0 text-sm text-n-slate-11" data-waslo-week-email-to>
            {{ t('WASLO.OVERVIEW.WEEK.EMAIL_TO', { address: email.address }) }}
          </p>
        </template>
        <p
          v-if="saving"
          class="mb-0 text-sm text-n-slate-11"
          data-waslo-week-email-saving
        >
          {{ t('WASLO.OVERVIEW.WEEK.EMAIL_SAVING') }}
        </p>
        <Banner v-else-if="failed" color="ruby" data-waslo-week-email-error>
          {{ t('WASLO.OVERVIEW.WEEK.EMAIL_FAILED') }}
        </Banner>
      </div>
    </CardLayout>
  </div>
</template>
